import { InitialConnectAiEvent } from '../ChatFramework.types';
import {
  BotConstants,
  buildConnectToAgentWithArticleConfig,
  checkAiABTestingPassed,
  checkAiTransactionABTestingPassed,
  createMessageHistory,
  EventType,
  getRandomInt,
  ItemType,
  spawnSystemMessage,
  spawnSystemMessageFromAI,
  spawnUserMessageForAI,
} from '../../lib';
import {
  AiMessageForUser,
  ArticleSystemOption,
  BaseEvent,
  BaseExternalEvent,
  ConnectToAgent,
  RateAiResponseEvent,
  ResolutionEvent,
  UserInputSubmittedTransactionForAiEvent,
  UserMessageForAI,
  UserMessageForAIEvent,
} from '../../types';
import { AI_EVENTS, analytics } from '@/common/logs';
import { reportingService } from '@/common/reporting';
import { isAiThresholdLimitError } from '@/common/error';
import * as PleasedApi from '@/common/api/api.pleased';
import { switcher, Switcher } from '../switcher';
import { deepClone, replacePlaceholders, waitFor } from '@/common/utils';
import { i18n } from '@/plugins/i18n';
import * as env from '@/common/environment';
import * as ApiModel from '@/common/api/models.pleased';
import { ResolutionType } from '../../enum/ResolutionType';
import { generateUpdatedEvent } from '../ChatFramework.utils';
import {
  isArticleFromListNavigationEvent,
  isArticleOptionEvent,
  isArticleSupportRequestEvent,
  isBrowseOtherArticlesEvent,
  isIssueNotListedArticlesEvent,
  isOverlongResolutionEvent,
  isSectionOptionEvent,
  isStaleConversationResolutionEvent,
} from './AiService.eventUtils';
import {
  AiServiceChatFrameworkInterface,
  GetAiResponseParams,
  HandleAiResponseActionParams,
  HandleAiResponseErrorParams,
  HandleAiResponseParams,
} from './AiService.types';
import { MessageType } from '../../enum';
import {
  getAiEventFinishFlowConfig,
  getAiFinishFlowConfig,
  isUserEligibleForAiFlow,
  setRecentlyConnectedAI,
} from './AiService.utils';
import { getAiActionSelectorTypes, isAiActionShowSelector } from './AiService.static';
import { WatchdogUtil } from '../WatchdogUtil';
import { getAiChatQueueMessages } from '../messages';
import { generateIssueMessage, TRANSACTION_TYPE_NAME_ENUM } from '@/common/transaction-utils';
export class AiService {
  private chatFramework: AiServiceChatFrameworkInterface;
  private stopFeedbackTimer: (() => void) | null = null;

  protected aiEventSwitch!: Switcher<BaseEvent | BaseExternalEvent>;
  protected aiConnectEventSwitch!: Switcher<BaseEvent>;
  protected aiConnectExternalEventSwitch!: Switcher<BaseExternalEvent>;

  private aiMessageCounter = 0;
  private AI_MESSAGE_LIMIT = 10;
  private readonly METADATA_MESSAGE_PREFIX = '[METADATA] ';

  constructor (chatFramework: AiServiceChatFrameworkInterface) {
    this.chatFramework = chatFramework;
    this.setupEventSwitch();
  }

  // Add new events here separately (without inner checks) for each "when" case, so we can
  // check if AI Service supports it.
  protected setupEventSwitch () {
    // This switcher relates to events AI Service need to handle – AI Related events
    this.aiEventSwitch = switcher<BaseEvent | BaseExternalEvent>()
      .when(EventType.isRateAiResponseEvent, this.handleRateAiResponseEvent)
      .when(EventType.isUserMessageForAIEvent, this.handleMessageFromUserForAI)
      .when(EventType.isUserInputSubmittedTransactionForAiEvent, this.handleUserTransactionMetadata)
      .none(e => console.error('[AiS] Unhandled event passed', { e }));

    // This switcher relates to BaseEvent AI Insertion points/Connect to AI
    this.aiConnectEventSwitch = switcher<BaseEvent>()
      .when(isSectionOptionEvent, this.onSectionSelectOption)
      .when(isArticleOptionEvent, this.onArticleSelectOption)
      .when(isArticleFromListNavigationEvent, this.onArticleSelectOption)
      .when(this.isBrowseOtherArticlesEventSupported, this.onArticleSelectOption)
      .when(isIssueNotListedArticlesEvent, this.onIssueNotListedArticles)
      .when(isOverlongResolutionEvent, this.handleOverlongAiConversationResolution)
      .when(isStaleConversationResolutionEvent, this.handleStaleAiConversation)
      .none(e => console.error('[AiS] Unhandled connect event passed', { e }));

    // This switcher relates to BaseExternalEvent AI Insertion points/Connect to AI
    this.aiConnectExternalEventSwitch = switcher<BaseExternalEvent>()
      .when(isArticleSupportRequestEvent, this.connectToAI)
      .none(e => console.error('[AiS] Unhandled connect external event passed', { e }));
  }

  public testAiEventHandler (event: BaseEvent | BaseExternalEvent) {
    return this.aiEventSwitch.test(event);
  }

  public submitAiEvent (event: BaseEvent | BaseExternalEvent) {
    this.aiEventSwitch.match(event);
  }

  public testAiConnectEventHandler (event: BaseEvent) {
    if (!this.checkCanConnectToAI(event)) return false;
    return this.aiConnectEventSwitch.test(event);
  }

  public submitAiConnectEvent (event: BaseEvent) {
    this.aiConnectEventSwitch.match(event);
  }

  public testAiConnectExternalEventHandler (event: BaseExternalEvent) {
    if (!EventType.isArticleInteractionEvent(event)) return false;
    if (!this.checkCanConnectToAI(event)) return false;
    return this.aiConnectExternalEventSwitch.test(event);
  }

  public submitAiConnectExternalEvent (event: BaseExternalEvent) {
    this.aiConnectExternalEventSwitch.match(event);
  }

  private checkCanConnectToAI (event: InitialConnectAiEvent) {
    const isExternalEvent = EventType.isExternalEvent(event);

    // AI TODO: separate properties getter based on event if we'll need more fields,
    // since currently we take properties related only for articles.
    // We can refactor it in a "switcher" way for preparing those properties based on event type and
    // use it before calling checkCanConnectToAI in tester functions
    const connectConfig = (isExternalEvent
      ? event.payload.supportOptions
      : (event as BaseEvent).payload?.selected) as ConnectToAgent;

    const {
      userId,
      aiEnabledPerBrand,
      aiEnabledPerSession,
      userLoyaltyTier,
      aiAbTestingEnabled,
      aiAbTestPercentage,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      openAiDown,
    } = this.chatFramework.getOptions();

    const canConnectToAI = isUserEligibleForAiFlow({
      userId,
      userLoyaltyTier,
      openAiDown,
      aiEnabledPerBrand,
      aiEnabledPerSession,
      aiAbTestingEnabled,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      abTestPercentage: aiAbTestPercentage,
      skipAiChat: connectConfig?.skipAiChat,
      enableMetadataRules: connectConfig?.enableMetadataRules,
    });

    return canConnectToAI;
  }

  protected connectToAI (event: InitialConnectAiEvent) {
    const isExternalEvent = EventType.isExternalEvent(event);

    const connectConfig = (isExternalEvent
      ? event.payload.supportOptions
      : (event as BaseEvent).message.config?.connect) as ConnectToAgent;

    const category = connectConfig?.category || '';
    const article = connectConfig?.articleId || ''; // Check if title is here

    const { userName, aiWelcomeMessage } = this.chatFramework.getOptions();

    const appName = env.get('appName') || '';
    const fallbackMessage = i18n.t('chat.msg.ai.welcome_default', {
      appName,
      categoryName: category,
      requesterName: userName,
    }) as string;

    const messageText = replacePlaceholders(aiWelcomeMessage || fallbackMessage, {
      category: { name: category },
      requester: { name: userName },
      app: { name: appName },
    });

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_SESSION_START,
      metadata: { message: messageText, category, article },
    });

    const initialMessage = spawnSystemMessageFromAI({
      message: messageText,
      sessionId: analytics.sessionId || '',
      initialEvent: event,
    });

    this.chatFramework.addMessage(initialMessage);
    this.chatFramework.emitter.emit('preConnectToAI');

    return true;
  }

  public async getAiResponse (params: GetAiResponseParams) {
    const { message, isFirstRequest, welcomeMessage, transactionMetadata } = params;
    const options = this.chatFramework.getOptions();

    const aiAbTestPassed = checkAiABTestingPassed({
      userId: options.userId,
      enableAbTesting: options.aiAbTestingEnabled,
      abValue: options.aiAbTestPercentage,
    });

    const aiTransactionAbTestPassed = checkAiTransactionABTestingPassed({
      userId: options.userId,
      enableAbTesting: options.aiTransactionEnabled,
      abValue: options.aiTransactionAbRatio,
      aiAbTestPassed,
    });

    const requestParams = {
      user_id: options.userId ? `${options.userId}` : '',
      user_input: message,
      session_id: analytics.sessionId || '',
      first_request: isFirstRequest,
      transaction_enable: aiTransactionAbTestPassed,
      ...(isFirstRequest
        ? {
          userLogs: reportingService.getLogs(),
          userSelections: getAiChatQueueMessages(this.chatFramework.getMessages()),
          welcomeMessage,
        }
        : {}),
      transactionMetadata,
    };

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_USER_REQUEST,
      metadata: { ...requestParams },
    });

    return PleasedApi.getAiChatResponse(requestParams);
  }

  private handleAiResponseAction (params: HandleAiResponseActionParams) {
    const { event, userMessage, aiResponse } = params;

    if (!aiResponse || !aiResponse.action) {
      return;
    }

    // TODO: if we'll have too many lines here, then separate handlers
    const actionSwitcher = switcher<ApiModel.AiResponseAction>()
      .when(ApiModel.AiResponseAction.CONNECT_TO_AGENT, () => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_CONNECT_SUPPORT_RESPONSE,
          metadata: { message: userMessage, aiResponse },
        });

        const finishFlowConnectConfig = getAiEventFinishFlowConfig(event);
        this.connectToAgentFromAiFlow(finishFlowConnectConfig);
      })
      .when(ApiModel.AiResponseAction.END_CHAT, () => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_END_CHAT_RESPONSE,
          metadata: { message: userMessage, aiResponse },
        });

        this.finishAiFlowPerSession();
        this.chatFramework.emitter.emit('endAiConversation');
      })
      .when(isAiActionShowSelector, action => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_SHOW_TRANSACTION_SELECTOR,
          metadata: { message: userMessage, aiResponse },
        });

        const transactionTypes = getAiActionSelectorTypes(action);
        this.chatFramework.emitter.emit('showTransactionSelector', transactionTypes);
      })
      .none(() => {
        console.error('Unhandled action', aiResponse.action);
      });

    return actionSwitcher.match(aiResponse.action);
  }

  private handleRateAiResponseEvent = (event: RateAiResponseEvent) => {
    const {
      message,
      payload: { selected },
    } = event;

    this.chatFramework.emitter?.emit('rateAiResponse', {
      message,
      rate: selected,
    });
  };

  private handleOverlongAiConversationResolution = (event: ResolutionEvent) => {
    const { message } = event;
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      analytics.trackAiEvent({
        name: AI_EVENTS.AI_OVERLONG_CONVERSATION_CONNECT_SUPPORT,
        metadata: { message: message.message },
      });

      this.chatFramework.connectArticleWithAcknowledgement({
        supportOptions: message.config?.connect as ConnectToAgent,
        event,
        history: createMessageHistory(event, message),
      });
    } else if (selected === ResolutionType.NO) {
      this.chatFramework.removeMessage(message);
    }
  };

  private onSectionSelectOption = (event: BaseEvent) => {
    const selected = event.payload.selected;

    this.chatFramework.emitter?.emit('selectOption', {
      selected,
      message: event.message,
      event,
    });

    this.connectToAI(generateUpdatedEvent(event, selected));
  };

  private onArticleSelectOption = (event: BaseEvent) => {
    const selected = event.payload.selected as ArticleSystemOption;

    this.chatFramework.emitter?.emit('selectOption', {
      selected,
      message: event.message,
      event,
    });

    // For article only case – isArticleOption, move to separate function
    const parentWithArticleConfig = buildConnectToAgentWithArticleConfig(event.message?.config?.connect!, selected);
    const updatedEvent = generateUpdatedEvent(event, parentWithArticleConfig);

    this.connectToAI(updatedEvent);
  };

  private isBrowseOtherArticlesEventSupported (event: BaseEvent) {
    const isCorrectEvent = isBrowseOtherArticlesEvent(event);
    if (!isCorrectEvent) return false;

    // Extra check for this option to be show per certain ENVs
    const { showInBrowseOtherArticles } = env.get('AI') || {
      showInBrowseOtherArticles: false,
    };

    return Boolean(showInBrowseOtherArticles);
  }

  private onIssueNotListedArticles = (event: BaseEvent) => {
    const selected = event.payload.selected;

    this.chatFramework.emitter?.emit('selectOption', {
      selected,
      message: event.message,
      event,
    });

    const hardcodedConnectConfigParams = { showDescriptionBox: true };
    const updatedEvent = generateUpdatedEvent(event, hardcodedConnectConfigParams);

    this.connectToAI(updatedEvent);
  };

  private getInitialAiMessage (): AiMessageForUser | undefined {
    return this.chatFramework
      .getMessages()
      .find(m => ItemType.isAiMessageForUser(m) && m.initialEvent) as AiMessageForUser;
  }

  private checkIsFirstRequestToAI (): boolean {
    return this.chatFramework.getMessages().filter(m => ItemType.isAiMessageForUser(m)).length === 1;
  }

  private getLastUserMessageForAI (): UserMessageForAI | undefined {
    return [...this.chatFramework.getMessages()]
      .reverse()
      .find(m => ItemType.isUserMessageForAI(m)) as UserMessageForAI;
  }

  private prepareConnectedAiEvent (
    initialEvent: InitialConnectAiEvent
  ): {
    connectedAiEvent: InitialConnectAiEvent;
    connectConfig: ConnectToAgent;
  } {
    const isExternalEvent = EventType.isExternalEvent(initialEvent);
    const hardcodedConnectConfigParams = { aiConversation: true };
    const connectedAiEvent = deepClone(initialEvent);

    if (isExternalEvent) {
      connectedAiEvent.payload.supportOptions = {
        ...connectedAiEvent.payload.supportOptions,
        ...hardcodedConnectConfigParams,
      };
    } else if (EventType.isSystemEvent(connectedAiEvent)) {
      connectedAiEvent.message.config = {
        ...connectedAiEvent.message.config,
        connect: {
          ...connectedAiEvent.message.config?.connect!,
          ...hardcodedConnectConfigParams,
        },
      };
    }

    const connectConfig = (isExternalEvent
      ? connectedAiEvent.payload.supportOptions
      : (connectedAiEvent as BaseEvent).message.config?.connect) as ConnectToAgent;

    return { connectedAiEvent, connectConfig };
  }

  private createUserMessageForAI (message: string, initialEvent: InitialConnectAiEvent): UserMessageForAI {
    const userMessage = spawnUserMessageForAI({
      message,
      initialEvent,
      sessionId: analytics.sessionId || '',
    }) as UserMessageForAI;

    this.aiMessageCounter += 1;
    this.chatFramework.addMessage(userMessage);

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_USER_INPUT,
      selection: userMessage.id,
      metadata: { message: userMessage.message, messageId: userMessage.id },
    });

    return userMessage;
  }

  private async handleFirstResponse (
    aiResponse: ApiModel.AiChatResponsePayload, // adjust type as needed
    isFirstRequest: boolean,
    connectConfig: ConnectToAgent
  ): Promise<AiMessageForUser | null> {
    if (!aiResponse) return null;

    const options = this.chatFramework.getOptions();

    const aiResponseMessage = spawnSystemMessageFromAI({
      message: aiResponse.response[0],
      sessionId: analytics.sessionId || '',
      aiResponse,
    });

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_RESPONSE_FIRST,
      selection: aiResponseMessage.id,
      metadata: {
        message: aiResponseMessage.message,
        messageId: aiResponseMessage.id,
        aiResponse,
      },
    });

    if (isFirstRequest) {
      this.chatFramework.emitter.emit('connectedToAI', { connectConfig });
      setRecentlyConnectedAI(options.userId);
    }

    return aiResponseMessage;
  }

  private async handleFollowUpResponses (
    aiResponse: ApiModel.AiChatResponsePayload
  ): Promise<ApiModel.AiChatResponsePayload> {
    if (!aiResponse || aiResponse.response.length <= 1) return aiResponse;

    for (let i = 1; i < aiResponse.response.length; i++) {
      const delayBeforeMessage = getRandomInt(1, 4) * 1000;
      const delayPromise = waitFor(delayBeforeMessage);

      await this.chatFramework.addMessage(
        delayPromise.then(() => {
          const aiResponseMessage = spawnSystemMessageFromAI({
            message: aiResponse.response[i],
            sessionId: analytics.sessionId || '',
            aiResponse,
          });

          analytics.trackAiEvent({
            name: AI_EVENTS.AI_RESPONSE_FOLLOW_UP,
            selection: aiResponseMessage.id,
            metadata: {
              messageIndex: i,
              message: aiResponseMessage.message,
              messageId: aiResponseMessage.id,
              aiResponse,
            },
          });

          return aiResponseMessage;
        })
      );
    }

    return aiResponse;
  }

  private handleOverlongConversation (aiResponse: ApiModel.AiChatResponsePayload, connectConfig: ConnectToAgent): void {
    if (!aiResponse) return;
    // Skip overlong conversation if AI response has action
    if (aiResponse.action) return;

    this.aiMessageCounter += aiResponse.response.length;
    if (this.aiMessageCounter < this.AI_MESSAGE_LIMIT) return;

    const resolutionConnectConfig = getAiFinishFlowConfig(connectConfig);
    this.aiMessageCounter = 0;
    this.chatFramework.addMessage(
      spawnSystemMessage({
        type: MessageType.OVERLONG_AI_CONVERSATION_RESOLUTION,
        message: i18n.t('chat.sys.msg.ai.overlong_conversation') as string,
        config: { connect: resolutionConnectConfig },
      })
    );
  }

  private handleMessageFromUserForAI = async (event: UserMessageForAIEvent) => {
    const { message } = event.payload;

    this.stopFeedbackTimer?.();
    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    // Determine initialMessage with event/connectConfig
    const initialMessage = this.getInitialAiMessage();
    const initialEvent = initialMessage?.initialEvent;

    if (!initialMessage || !initialEvent) {
      console.error('[CF] No initialEvent found for AI message');
      return;
    }

    try {
      // Add aiConversation flag here to connect config
      const { connectedAiEvent } = this.prepareConnectedAiEvent(initialEvent);

      // We save connectedAiEvent in message here, since in case of error this message will be removed
      this.createUserMessageForAI(message, connectedAiEvent);

      const isFirstRequest = this.checkIsFirstRequestToAI();

      // We use initial event here, without any modifications if case of error
      const aiResponsePromise = this.getAiResponse({
        message,
        isFirstRequest,
        welcomeMessage: initialMessage.message,
      });

      await this.handleAiResponse({ event: initialEvent, userMessage: message, aiResponsePromise });
    } finally {
      this.chatFramework.emitter.emit('disableUserInputForAi', false);
    }
  };

  private handleUserTransactionMetadata = (event: UserInputSubmittedTransactionForAiEvent) => {
    console.log('handleUserTransactionMetadata', event);
    const { transactionType, transactionMetadata } = event.payload;

    const transactionTypeName = TRANSACTION_TYPE_NAME_ENUM[transactionType];
    const isTransactionSelected = !transactionMetadata.transactionNotSelected;
    // This is required to pass type issue due to low version of typescript
    const selectedTransaction = transactionMetadata as ApiModel.ExtendedSelectedTransStatement;

    const message = (isTransactionSelected ? i18n.t('chat.sys.msg.ai.transaction_help', {
      transactionType: transactionTypeName,
      transactionId: selectedTransaction.tradeId,
    }) : i18n.t('chat.sys.msg.ai.transaction_not_listed_help')) as string;

    this.chatFramework.addMessage(
      spawnUserMessageForAI({
        message,
      })
    );

    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    // Determine initialMessage with event/connectConfig
    const initialMessage = this.getInitialAiMessage();
    const initialEvent = initialMessage?.initialEvent;

    if (!initialMessage || !initialEvent) {
      console.error('[CF] No initialEvent found for AI message');
      return;
    }

    try {
      // We use initial event here, without any modifications if case of error
      const aiResponsePromise = this.getAiResponse({
        message: `${this.METADATA_MESSAGE_PREFIX}${message}`,
        isFirstRequest: false,
        transactionMetadata,
      });

      this.handleAiResponse({ event: initialEvent, userMessage: null, aiResponsePromise });
    } finally {
      this.chatFramework.emitter.emit('disableUserInputForAi', false);
    }
  };

  // We need to handle Promise here for "Typing" message
  private handleAiResponse = async (params: HandleAiResponseParams) => {
    const { event, userMessage, aiResponsePromise } = params;

    try {
      // Add aiConversation flag here to connect config
      const { connectConfig, connectedAiEvent } = this.prepareConnectedAiEvent(event);
      const isFirstRequest = this.checkIsFirstRequestToAI();

      // We're adding "Typing" message for first result here.
      this.chatFramework.addMessage(
        aiResponsePromise.then(aiResponse => this.handleFirstResponse(aiResponse, isFirstRequest, connectConfig))
      );

      const aiResponse = await aiResponsePromise;

      // If there're multiple responses, we're adding them with delay between messages
      this.handleFollowUpResponses(aiResponse)
        // Handle AI Action if any provided
        .then(() => this.handleAiResponseAction({ event: connectedAiEvent, userMessage, aiResponse }))
        // Once all AI Messages added, we check for overlong conversation, if no action passed.
        .then(() => this.handleOverlongConversation(aiResponse, connectConfig));

      // Clear a previous feedback timer if any, setup a new one
      this.stopFeedbackTimer?.();
      this.startTimedFeedback();
    } catch (error) {
      this.handleAiResponseError({ event, userMessage, error: error as Error });
    }
  };

  private handleAiResponseError = (params: HandleAiResponseErrorParams) => {
    const { event, userMessage, error } = params;

    console.error('Error in AI flow', error);
    analytics.trackAiEvent({
      name: AI_EVENTS.AI_ERROR_RESPONSE,
      metadata: { message: userMessage, error },
    });

    const isThresholdError = isAiThresholdLimitError(error as Error);
    const isFirstRequest = this.checkIsFirstRequestToAI();
    const resumeDefaultChatFlow = (!EventType.isExternalEvent(event) && isThresholdError) || isFirstRequest;

    // If it's error for first request or threshold,
    // we need to remove AI message and resume default chat flow
    if (resumeDefaultChatFlow) {
      const lastUserMessageForAI = this.getLastUserMessageForAI();
      this.chatFramework.removeMessage(lastUserMessageForAI);
      this.finishAiFlowPerSession();
      this.chatFramework.emitter.emit('resumeChatFlow');
      this.chatFramework.submit(event as BaseEvent);
      return;
    }

    // Otherwise show error message and connect to agent
    const finishFlowConnectConfig = getAiEventFinishFlowConfig(event);
    this.connectToAgentFromAiFlow(finishFlowConnectConfig);

    this.chatFramework.addMessage(
      spawnSystemMessage({
        type: MessageType.CHAT_MESSAGE,
        message: i18n.t('chat.sys.msg.ai.response_error') as string,
      })
    );
  };

  private finishAiFlowPerSession () {
    this.chatFramework.setOptions({ aiEnabledPerSession: false });
    this.stopFeedbackTimer?.();
  }

  private connectToAgentFromAiFlow (connectConfig: ConnectToAgent) {
    this.finishAiFlowPerSession();
    this.chatFramework.connectToAgentFromConfig(connectConfig);
  }

  // method to start timed ai feedback
  private startTimedFeedback () {
    const options = this.chatFramework.getOptions();
    const { enableTimedAiFeedback, timedAiFeedbackNoMessagesTimeInSeconds = 0 } = options;

    // This should not be a case, but we'll keep it here for safety
    if (this.stopFeedbackTimer) {
      return;
    }

    if (!enableTimedAiFeedback || !timedAiFeedbackNoMessagesTimeInSeconds) {
      return;
    }

    const stopTimer = WatchdogUtil.startWatchdog(
      timedAiFeedbackNoMessagesTimeInSeconds * 1000,
      5000,
      this.handleTimedFeedback
    );

    this.stopFeedbackTimer = () => {
      stopTimer();
      this.stopFeedbackTimer = null;
    };
  }

  private handleTimedFeedback = () => {
    const { timedAiFeedbackConfirmationMessageText } = this.chatFramework.getOptions();

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_STALE_CONVERSATION,
      metadata: { text: timedAiFeedbackConfirmationMessageText },
    });

    this.stopFeedbackTimer?.();
    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    this.chatFramework.addMessage(
      spawnSystemMessage({
        type: MessageType.STALE_AI_CONVERSATION_RESOLUTION,
        message: timedAiFeedbackConfirmationMessageText,
      })
    );
  };

  private handleStaleAiConversation = (event: ResolutionEvent) => {
    // Allow user to type and show resume conversation message
    if (event.payload.selected === ResolutionType.YES) {
      const { timedAiFeedbackResumeMessageText } = this.chatFramework.getOptions();

      analytics.trackAiEvent({
        name: AI_EVENTS.AI_STALE_CONVERSATION_RESUME,
        metadata: { text: timedAiFeedbackResumeMessageText },
      });

      this.chatFramework.emitter.emit('disableUserInputForAi', false);
      this.chatFramework.addMessage(
        spawnSystemMessage({
          type: MessageType.CHAT_MESSAGE,
          message: timedAiFeedbackResumeMessageText,
        })
      );
      // Otherwise show csat
    } else if (event.payload.selected === ResolutionType.NO) {
      analytics.trackAiEvent({
        name: AI_EVENTS.AI_STALE_CONVERSATION_END,
      });

      this.finishAiFlowPerSession();
      this.chatFramework.emitter.emit('endAiConversation');
      this.chatFramework.emitter.emit('endChat');
    }
  };
}
